import { FC, useContext, useEffect, useState, isValidElement } from "react";
import { useNavigate } from "react-router";
import Container from "core/components/layout/container/container";
import Pageloading from "core/components/routing/pages/pageloading/pageloading";
import SessionTimeout from "core/components/routing/pages/sessionTimeout/sessionTimeout";
import { AuthContext } from "core/contexts/auth/auth";
import { setHeader } from "core/store/shared/header";
import { useAppDispatch } from "core/store/reduxStore";
import { PageloadingContainer } from "./pages/pageloading/pageloading.styled";
import AccessDenied from "./pages/accessDenied/accessDenied";

interface SessionCheckProps {
  Component: any;
  title?: string;
  requiredPermissions: string[];
}

const checkPermissions = (requiredGroups: string[], userGroups: string[]) => {
  const response =
    requiredGroups.some((group) => userGroups.includes(group)) ||
    requiredGroups.length === 0;
  return response;
};

const SessionCheck = ({
  Component,
  title,
  requiredPermissions,
}: SessionCheckProps) => {
  const { amsUser, checkAMSToken, isAvailableAuthUser } =
    useContext(AuthContext);
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useAppDispatch();
  
  // Check if we can still grab a new token from the backend, if so we are still logged in
  async function checkToken() {
    try {
      // Add a retry mechanism for token verification during navigation
      let retryCount = 0;
      const maxRetries = 2;
      let token = null;
      
      while (retryCount <= maxRetries && !token) {
        token = await checkAMSToken();
        
        if (token) {
          // Success - we have a valid token
          break;
        }
        
        retryCount++;
        
        if (retryCount <= maxRetries) {
          // Wait a moment before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      if (token) {
        setIsAuthenticated(
          checkPermissions(requiredPermissions, amsUser?.groups || [])
        );
        setIsLoading(false);
      } else {
        // Multiple failures - redirect to login
        console.log('Failed to verify token after multiple attempts');
        navigate("/console/login");
      }
    } catch (error) {
      console.error('Error during token verification:', error);
      navigate("/console/login");
    }
  }

  // Navigation Route Change (Not to Confuse With App Enter)
  useEffect(() => {
    checkToken();
  }, [title]);

  // Update the title in the headers
  useEffect(() => {
    if (title && title !== "") {
      dispatch(setHeader(title));
    }
  }, [title]);

  const renderPage = (): JSX.Element => {
    // Render the page
    return (
      <>
        {checkPermissions(requiredPermissions, amsUser?.groups || []) ===
        true ? (
          <Container>
            {isValidElement(Component) ? Component : <Component />}
          </Container>
        ) : (
          <Container>
            <AccessDenied />
          </Container>
        )}
      </>
    );
  };

  return (
    <>
      {isAvailableAuthUser && renderPage()}
      <SessionTimeout />
    </>
  );
};

export default SessionCheck;
